import React, { useState, useEffect } from 'react';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import SendIcon from '@mui/icons-material/Send';
import { Card, FormControl, Grid, IconButton, InputAdornment, InputLabel, FormHelperText, OutlinedInput, useMediaQuery, useTheme, Paper } from '@mui/material';
import { Visibility, VisibilityOff, Password } from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { showAlert } from '../../redux/create-actions/AlertAction';
import { useNavigate } from 'react-router-dom';
import { closeLoadingApi, openLoadingApi } from '../../redux/create-actions/LoadingAction';
import Color from '../../layouts/Color';
import FontSize from '../../layouts/FontSize';
import { graphUserChangePass } from '../../services/Authenticate';
import Storage from '../../services/Storage';

const validationSchema = Yup.object().shape({
    currentPassword: Yup.string()
      .required("Please provide a valid password"),
    newPassword: Yup.string()
      .required("This field is required")
      .min(8, 'Password is too short - should be 8 chars minimum.'),
    confirmPassword: Yup.string()
      .when("newPassword", {
        is: val => (val && val.length > 0 ? true : false),
        then: Yup.string().oneOf(
          [Yup.ref("newPassword")],
          "Both password need to be the same"
        )
      })
  });

const paperStyle = { my: { xs: 1, md: 2 }, p: { xs: 1, md: 3 }, minHeight: '100%'};
const container = { mt: 2};
const buttonSubmit = {
    marginTop: 5,
    color: Color.Button,
    backgroundColor: Color.Dangerous,
    '&:hover': { background: Color.Button, color: Color.Dangerous }
};
const titleStyle = { color: Color.Main, pt: 2};
const boxStyle = {
    borderRadius: '5px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: 2
}

export default function ChangePassForm(props) {
    const theme = useTheme()
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const matchDownSM = useMediaQuery(theme.breakpoints.down('sm'));
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
    useEffect(() => {
      if (Storage.getLocalAccessToken() === null) {
        navigate('/signin', { replace: true })
      }
    }, [navigate])
    const formik = useFormik({
      initialValues: {
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      },
      validationSchema: validationSchema,
      onSubmit: (values) => { handleSubmit(values) }
    });
  
  
    const handleSubmit = (e) => {
      dispatch(openLoadingApi());
      let alert = {
        anchorOrigin: {
          vertical: 'top',
          horizontal: 'center'
        },
        isOpen: true,
        severity: 'error',
        title: 'Error',
        message: '',
      };

      graphUserChangePass(e.currentPassword, e.newPassword)
        .then(result => {
            navigate("/dashboard", { replace: true });
            alert = { ...alert, message: "Change password success!", severity: 'success', title: 'Success' };
            dispatch(closeLoadingApi());
            dispatch(showAlert(alert));  
        }).catch(error => {
          const err = JSON.parse(JSON.stringify(error));
          alert = {...alert, message: err.response.errors[0].message};
          dispatch(showAlert(alert));
          dispatch(closeLoadingApi());
        });
    }
  
    return (
        <Paper variant="outlined" sx={paperStyle} >
            <Grid container alignContent="center" justifyContent="space-between">
                <Grid item>
                    <Button id="set-pin" endIcon={<Password />} sx={{ color: theme.typography.h3, cursor: 'default', fontSize: FontSize.Normal }}>
                    Change Password
                    </Button>
                </Grid>
            </Grid>
        <Card>
          <Typography
            variant={matchDownSM ? 'h3' : 'h2'}
            sx={titleStyle} gutterBottom>
            Change Password
          </Typography>
          <Box
            paddingTop="30px"
            paddingBottom=" 10px"
            paddingRight={matchDownSM ? '16px' : '35px'}
            paddingLeft={matchDownSM ? '16px' : '35px'}
            sx={boxStyle}
          >
            <form component="form" onSubmit={formik.handleSubmit} noValidate>
              <Typography variant="h4" color={Color.Main}>
                Your new password must be different from previous used passwords: </Typography>
              <Grid sx={container} >
                <FormControl fullWidth
                  error={formik.touched.currentPassword && Boolean(formik.errors.currentPassword)}
                >
                  <InputLabel htmlFor=
                    "currentPassword">Current Password</InputLabel>
                  <OutlinedInput
                    id="currentPassword"
                    type={showCurrentPassword ? 'text' : 'password'}
                    fullWidth
                    autoComplete="currentPassword"
                    autoFocus
                    label="Current Password"
                    size={matchDownSM ? 'small' : 'large'}
                    value={formik.values.currentPassword}
                    onChange={formik.handleChange}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          color='primary'
                          onClick={e => { setShowCurrentPassword(!showCurrentPassword) }}
                          onMouseDown={(e) => e.preventDefault()}
                          edge="end"
                          size="small"
                        >
                          {showCurrentPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    }
                    inputProps={{}}
                    sx={{
                      '& .MuiInputBase-input': {
                        bgcolor: theme.palette.background.default
                      },
                      bgcolor: theme.palette.background.default,
                    }}
                  />
                  {formik.touched.currentPassword && formik.errors.currentPassword && (
                    <FormHelperText error id="standard-weight-helper-text-email-login">
                      {formik.errors.currentPassword}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid sx={container} >
                <FormControl fullWidth
                  error={formik.touched.newPassword && Boolean(formik.errors.newPassword)}
                >
                  <InputLabel htmlFor=
                    "password"> New Password</InputLabel>
                  <OutlinedInput
                    id="newPassword"
                    type={showPassword ? 'text' : 'password'}
                    fullWidth
                    required
                    label="New Password"
                    size={matchDownSM ? 'small' : 'large'}
                    value={formik.values.newPassword}
                    onChange={formik.handleChange}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          color='primary'
                          onClick={e => { setShowPassword(!showPassword) }}
                          onMouseDown={(e) => e.preventDefault()}
                          edge="end"
                          size="small"
                        >
                          {showPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    }
                    inputProps={{}}
                    sx={{
                      '& .MuiInputBase-input': {
                        bgcolor: theme.palette.background.default
                      },
                      bgcolor: theme.palette.background.default,
                    }}
                  />
                  {formik.touched.newPassword && formik.errors.newPassword && (
                    <FormHelperText error id="standard-weight-helper-text-email-login">
                      {formik.errors.newPassword}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid sx={container}>
                <FormControl fullWidth
                  error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
                >
                  <InputLabel htmlFor=
                    "confirmPassword">Confirm Password</InputLabel>
                  <OutlinedInput
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    fullWidth
                    variant="contained"
                    size={matchDownSM ? 'small' : 'large'}
                    required
                    value={formik.values.confirmPasswordl}
                    onChange={formik.handleChange}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          color='primary'
                          onClick={e => { setShowConfirmPassword(!showConfirmPassword) }}
                          onMouseDown={(e) => e.preventDefault()}
                          edge="end"
                          size="small"
                        >
                          {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    }
                    label="Confirm Password"
                    inputProps={{}}
                    sx={{
                      '& .MuiInputBase-input': {
                        bgcolor: theme.palette.background.default
                      },
                      bgcolor: theme.palette.background.default,
                    }}
                  />
                  {formik.touched.confirmPassword && formik.errors.confirmPassword && (
                    <FormHelperText error id="standard-weight-helper-text-email-login">
                      {formik.errors.confirmPassword}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Button
                type="submit"
                variant="contained"
                endIcon={<SendIcon />}
                size={matchDownSM ? 'small' : 'medium'}
                sx={buttonSubmit}
              >
                Submit
              </Button>
            </form>
          </Box>
        </Card>
      </Paper>
      )
}